import { useSyncExternalStore } from 'react';

// The WORKING SET (records/RELATION_DRAWING_PREP.md R2) and the TRAIL (R7) —
// both CLIENT-SIDE by ruling: a pin is a view preference and a visit is a
// breadcrumb, not claims about the world, so neither gets a table (the
// permissive-storage warning sign is a feature that wants one). localStorage,
// one key each, capped.
//
// The working set is the pair page's FIRST source, shipped alone on purpose:
// it is the only source with no ranker dependency, so "this page is hard to
// use" cannot be confounded with "these pairs were not worth relating". The
// trail is the "where am I" half — a persistent record of recently visited
// objects across surfaces, and the cheapest honest locator: it feeds the
// working set (a trail row can be pinned) rather than being its own surface.

const PINS_KEY = 'wv-working-set';
const TRAIL_KEY = 'wv-trail';

// Pairs grow quadratically — 12 pins is already 66 possible pairs, which is
// more than a sitting relates. The cap is a working-memory bound, not a
// feature limit; oldest pin drops first.
const PIN_CAP = 12;
const TRAIL_CAP = 20;

const read = (key) => {
  try {
    const raw = localStorage.getItem(key);
    const val = raw ? JSON.parse(raw) : [];
    return Array.isArray(val) ? val : [];
  } catch {
    return [];
  }
};

// snapshot caches so useSyncExternalStore sees a stable reference between
// changes (a fresh array every call would loop the render)
let pinsCache = read(PINS_KEY);
let trailCache = read(TRAIL_KEY);

const write = (key, list) => {
  try {
    localStorage.setItem(key, JSON.stringify(list));
  } catch {
    /* storage full or blocked — the set just doesn't persist */
  }
  if (key === PINS_KEY) pinsCache = list;
  else trailCache = list;
  window.dispatchEvent(new Event('wv-working-set'));
};

const subscribe = (cb) => {
  // same-tab changes via the custom event; other tabs via 'storage'
  window.addEventListener('wv-working-set', cb);
  const onStorage = (e) => {
    if (e.key === PINS_KEY || e.key === TRAIL_KEY) {
      pinsCache = read(PINS_KEY);
      trailCache = read(TRAIL_KEY);
      cb();
    }
  };
  window.addEventListener('storage', onStorage);
  return () => {
    window.removeEventListener('wv-working-set', cb);
    window.removeEventListener('storage', onStorage);
  };
};

const entry = (obj) => ({
  id: obj.id,
  content: obj.content || '',
  object_type: obj.object_type || 'claim',
});

export const getPins = () => pinsCache;
export const isPinned = (id) => pinsCache.some((p) => p.id === id);

export function togglePin(obj) {
  const cur = getPins();
  const next = cur.some((p) => p.id === obj.id)
    ? cur.filter((p) => p.id !== obj.id)
    : [...cur, entry(obj)].slice(-PIN_CAP);
  write(PINS_KEY, next);
}

// Idempotent add, for callers that mean "make sure this is pinned" rather than
// "flip it" — the capture modal pins what it just minted, and a toggle there
// would silently UNPIN on a retry.
export function pin(obj) {
  if (!obj?.id || isPinned(obj.id)) return;
  write(PINS_KEY, [...getPins(), entry(obj)].slice(-PIN_CAP));
}

export const unpin = (id) => write(PINS_KEY, getPins().filter((p) => p.id !== id));
export const clearPins = () => write(PINS_KEY, []);

export function recordVisit(obj) {
  if (!obj?.id) return;
  const cur = trailCache.filter((t) => t.id !== obj.id); // re-visit moves to front
  write(TRAIL_KEY, [entry(obj), ...cur].slice(0, TRAIL_CAP));
}

export const getTrail = () => trailCache;

export const usePins = () => useSyncExternalStore(subscribe, getPins);
export const useTrail = () => useSyncExternalStore(subscribe, getTrail);
